/**
 * Professional Profile Guard — requires PROFESSIONAL role plus professional tier (admin bypass).
 */

import React, { useState, useEffect } from 'react';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { useSubscription } from '@/hooks/use-subscription';
import { hasRole, normalizeRoles } from '@/lib/normalize-user-data';
import { hasFeatureAccess, FeatureAccess, getUpgradeSuggestion } from '@/lib/subscription-feature-guards';
import { Button } from '@/components/ui/button';
import { Users } from 'lucide-react';
import { UpgradeModal } from '@/components/modals/UpgradeModal';
import { usePanelAccess } from '@/hooks/use-panel-access';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';

interface ProfessionalProfileGuardProps {
  children: React.ReactNode;
  showUpgradeCard?: boolean;
  customMessage?: string;
  /** Post-login return path (e.g. professional panel vs get-booked flow). */
  authReturnTo?: string;
}

export function ProfessionalProfileGuard({
  children,
  showUpgradeCard = true,
  customMessage,
  authReturnTo = "/connect/get-booked",
}: ProfessionalProfileGuardProps) {
  const { user, isLoading } = useFirebaseAuth();
  const { subscriptionTier, subscriptionStatus, planName, loading: subscriptionLoading } = useSubscription();
  const { canAccessProfessionalPanel, loading: panelAccessLoading } = usePanelAccess();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  // Redirect unauthenticated users to auth with explanation
  useEffect(() => {
    if (!isLoading && !user) {
      const isPanel = authReturnTo.includes("/professional/");
      toast({
        title: "Sign in required",
        description: isPanel
          ? "Please sign in to open your professional panel."
          : "Please sign in or create an account to set up your professional profile.",
        variant: "destructive",
      });
      navigate(`/auth?returnTo=${encodeURIComponent(authReturnTo)}`);
    }
  }, [isLoading, user, authReturnTo, navigate, toast]);

  const normalizedRoles = normalizeRoles(user);
  const hasProfessionalAccess = canAccessProfessionalPanel;

  if (isLoading || !user) {
    return null;
  }

  // While subscription/profile access checks load, keep children mounted so long flows
  // are not torn down and reset when entitlement data arrives.
  if (subscriptionLoading || panelAccessLoading) {
    return <>{children}</>;
  }

  // Strict tier gate: access ends the moment subscription ends. Users who built
  // a profile under a prior paid period must re-subscribe to edit it. Their
  // Firestore document is preserved untouched — only edit access is gated.
  if (hasProfessionalAccess) {
    return <>{children}</>;
  }

  const upgradeInfo = getUpgradeSuggestion(subscriptionTier, FeatureAccess.BE_BOOKED_PROFESSIONAL);

  if (showUpgradeCard) {
    return (
      <UpgradeModal
        isOpen={true}
        onClose={() => window.history.back()}
        onUpgrade={() => {}}
        requiredTier="Nobility"
        featureName="Professional Profile Creation"
        userTier={planName || 'Free'}
        userRoles={normalizedRoles}
        rawUserData={{
          subscriptionTier: subscriptionTier || undefined,
          roles: user?.roles,
          email: user?.email
        }}
      />
    );
  }

  return null;
}

/**
 * Professional profile setup button guard
 */
export function ProfessionalSetupGuard({
  children,
  onUpgradeClick
}: {
  children: React.ReactNode;
  onUpgradeClick?: () => void;
}) {
  const { user } = useFirebaseAuth();
  const [, navigate] = useLocation();
  const { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus, planName, loading: subscriptionLoading } = useSubscription();
  const isAdmin = hasRole(user, "ADMIN");
  const hasProfessionalRole = hasRole(user, "PROFESSIONAL");
  const hasProfessionalTier = hasFeatureAccess(
    subscriptionTier,
    FeatureAccess.BE_BOOKED_PROFESSIONAL,
    { subscriptionStatus }
  );
  const hasProfessionalAccess = isAdmin || hasProfessionalTier;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const normalizedRoles = normalizeRoles(user);
  const hasOnlyUserRole =
    normalizedRoles.length === 1 && normalizedRoles.includes("USER");

  // Handle click with permission check
  const handleClick = () => {
    console.log('🎭 GET BOOKED Button Click:', {
      action: 'GET_BOOKED_BUTTON_CLICKED',
      RAW_USER_DATA: {
        subscriptionTier: subscriptionTier || undefined,
        roles: user?.roles,
        email: user?.email
      },
      PROCESSED_DATA: {
        subscriptionTier: subscriptionTier || 'FREE',
        planName,
        isNobility,
        isRoyalty,
        isImperial,
        isAdmin,
        hasProfessionalRole,
        hasProfessionalTier,
        userRoles: normalizedRoles,
        hasOnlyUserRole,
        hasProfessionalAccess
      }
    });

    // Plan still loading: the get-booked page's guard waits for it.
    if (hasProfessionalAccess || subscriptionLoading) {
      navigate('/connect/get-booked');
    } else {
      setShowUpgradeModal(true);
      return;
    }
  };

  if (hasProfessionalAccess) {
    return <>{children}</>;
  }

  return (
    <>
      <Button
        variant="outline"
        className="border-green-500/20 text-green-400 hover:bg-green-500/10 hover:border-green-400 transition-all"
        onClick={handleClick}
        title="Upgrade to Nobility, Royalty, or Imperial plan to become a professional instructor"
      >
        <Users className="h-4 w-4 mr-2" />
        Become a Professional Instructor
      </Button>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {}}
        requiredTier="Nobility"
        featureName="Professional Profile Creation"
        userTier={planName || 'Free'}
        userRoles={normalizedRoles}
        rawUserData={{
          subscriptionTier: subscriptionTier || undefined,
          roles: user?.roles,
          email: user?.email
        }}
      />
    </>
  );
}
